import { useCallback, useEffect, useState } from 'react';

import {
  DEFAULT_GEOMETRY,
  STACK_BELOW,
  clampGeometry,
  readGeometry,
  writeGeometry,
} from './geometry';
import type { ConsoleGeometry } from './geometry';

/**
 * Состояние геометрии консоли: доли колонок, ширина окна, режим раскладки.
 *
 * Ширина окна нужна ЗДЕСЬ, а не в CSS: пределы колонок заданы в пикселях, и
 * привести к ним долю можно только зная, от чего она берётся. Медиазапрос дал
 * бы ступеньку, а тут нужна непрерывная величина.
 */
export interface ConsoleGeometryApi {
  geometry: ConsoleGeometry;
  /** Ширина окна в пикселях; 0 до первого замера на сервере. */
  width: number;
  /** Ниже STACK_BELOW три колонки заменяются одной с переключателем. */
  stacked: boolean;
  setLeft: (fraction: number) => void;
  setRight: (fraction: number) => void;
  setPads: (fraction: number) => void;
  reset: () => void;
}

export function useConsoleGeometry(): ConsoleGeometryApi {
  // Сохранённая геометрия читается ОДИН РАЗ при создании состояния. Dashboard -
  // одностраничное приложение на Vite без серверной отрисовки, поэтому первый
  // кадр уже знает сохранённые ширины и колонки не прыгают после монтирования.
  const [geometry, setGeometry] = useState<ConsoleGeometry>(readGeometry);
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));

  useEffect(() => {
    const onResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const apply = useCallback(
    (next: ConsoleGeometry) => {
      const clamped = clampGeometry(next, width || 1);
      setGeometry(clamped);
      writeGeometry(clamped);
    },
    [width],
  );

  const setLeft = useCallback(
    (fraction: number) => {
      setGeometry((current) => {
        const next = clampGeometry({ ...current, left: fraction }, width || 1);
        writeGeometry(next);
        return next;
      });
    },
    [width],
  );

  const setRight = useCallback(
    (fraction: number) => {
      setGeometry((current) => {
        const next = clampGeometry({ ...current, right: fraction }, width || 1);
        writeGeometry(next);
        return next;
      });
    },
    [width],
  );

  const setPads = useCallback(
    (fraction: number) => {
      setGeometry((current) => {
        const next = clampGeometry({ ...current, pads: fraction }, width || 1);
        writeGeometry(next);
        return next;
      });
    },
    [width],
  );

  const reset = useCallback(() => {
    apply(DEFAULT_GEOMETRY);
  }, [apply]);

  // Приведение к пределам при изменении ширины окна: сохранённая доля,
  // законная на 1920, на 1100 даёт колонку уже минимума.
  const effective = width > 0 ? clampGeometry(geometry, width) : geometry;

  return {
    geometry: effective,
    width,
    stacked: width > 0 && width < STACK_BELOW,
    setLeft,
    setRight,
    setPads,
    reset,
  };
}
